import React, { useEffect, useState } from "react";
import Loader from "../../components/Loader";
import { useNavigate, useParams } from "react-router-dom";
import Calling from "../../api/Calling";
import { toast } from "react-toastify";
import { MdBlock, MdCheckCircle } from "react-icons/md";
import SaveButton from "../../components/ul/SaveButton";
import CancelButton from "../../components/ul/CancelButton";
import EditButton from "../../components/ul/EditButton";
import img1 from "../../Image/Profile.png";
import formatDateTime from "../../components/formatDateTime";
import { FaTrashAlt } from "react-icons/fa";
import generateCaptcha from "../../components/captcha";
import NotFound from "../../components/ul/NotFound";
import { IoIosEye } from "react-icons/io";
import { LuDot } from "react-icons/lu";

const UserInfo = () => {
  const [loader, setLoader] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [coinHistory, setCoinHistory] = useState([]);
  const [withdrawHistory, setWithdrawHistory] = useState([]);

  const { id } = useParams();
  const navigate = useNavigate();

  // ✅ Fetch user data
  const fetchUserData = async () => {
    setLoader(true);
    try {
      const response = await Calling.GetAllUserData(
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        id
      );
      if (response?.status) {
        const d = response?.data || {};
        setUserData({
          _id: d?._id || id,
          u_name: d?.u_name || "",
          u_email: d?.u_email || "",
          u_device: d?.u_device || "",
          u_coin:
            typeof d?.u_coin === "number" ? d.u_coin : Number(d?.u_coin) || 0,
          u_profile: d?.u_profile || "",
          u_status: Boolean(d?.u_status),
          last_login: d?.last_login || "",
          createdAt: d?.createdAt || "",
          updatedAt: d?.updatedAt || "",
          __v: d?.__v ?? undefined,
        });
        setCoinHistory(response?.coinHistory || []);
        setWithdrawHistory(response?.userWithdraw || []);
      } else {
        toast.error(response?.message || "Failed to fetch user data.");
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      toast.error("Unable to fetch user data.");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, [id]);

  // ✅ Validate email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email && !emailRegex.test(email)) {
      toast.error("Please provide a valid email.");
      return false;
    }
    return true;
  };

  // ✅ Save user changes
  const handleSaveChanges = async () => {
    if (!userData?.u_name || !userData?.u_email || userData?.u_coin === "") {
      return toast.error("All fields are required.");
    }

    if (!validateEmail(userData?.u_email)) return;

    if (userData?.u_coin < 0 || !Number.isInteger(Number(userData?.u_coin))) {
      return toast.error("Invalid coin value.");
    }

    setLoader(true);
    try {
      const body = {
        u_name: userData?.u_name,
        u_email: userData?.u_email,
        u_coin: Number(userData?.u_coin) || 0,
        u_device: userData?.u_device || "",
      };

      const response = await Calling.UpdateUserInfo(id, body);
      if (response?.status) {
        toast.success(response?.message || "User updated successfully!");
        setIsEditing(false);
        fetchUserData();
      } else {
        toast.error(response?.message || "Failed to save changes.");
      }
    } catch (error) {
      console.error("Error saving changes:", error);
      toast.error("An unexpected error occurred.");
    } finally {
      setLoader(false);
    }
  };

  // ✅ Block / Unblock user
  const handleBlockUser = async (id, status) => {
    setLoader(true);
    try {
      const response = await Calling.BlockUser(id, status);
      if (response?.status) {
        toast.success(response?.message || "User status updated!");
      } else {
        toast.error(response?.message || "Failed to update user status.");
      }
    } catch (error) {
      console.error("Error updating user status:", error);
      toast.error("Something went wrong while updating status.");
    } finally {
      setLoader(false);
      fetchUserData();
    }
  };

  // ✅ Delete user with captcha
  const handleDeleteUser = async (id) => {
    const content = `You are about to delete this user. To confirm, please solve this math problem.`;
    const check = generateCaptcha(content);

    if (!check) {
      toast.error("Captcha answer is incorrect", { autoClose: 2000 });
      return;
    }

    try {
      setLoader(true);
      const body = { id: [id] };
      const response = await Calling.DeleteUser(body);
      if (response?.status) {
        toast.success(response?.message || "User deleted successfully.");
        navigate("/manage/user/all/data");
      } else {
        toast.error(response?.message || "Failed to delete user.");
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error("Something went wrong while deleting.");
    } finally {
      setLoader(false);
    }
  };

  // ✅ Safe date formatting
  const safeFormat = (dt) => {
    try {
      return dt ? formatDateTime(dt) : "N/A";
    } catch {
      return "N/A";
    }
  };
  const handleWithdraw = (id) => {
    navigate(`/manage/withdraw/info/${id}`);
  };

  return (
    <>
      {loader || !userData ? (
        <Loader loader={loader} />
      ) : (
        <div className="w-full md:ml-[4.4rem] px-3 sm:px-4 lg:px-6 mt-16">
          <div className="bg-main-white rounded-xl mt-4 shadow-sm">
            <div className="p-4 md:p-6">
              <h1 className="text-xl md:text-2xl flex justify-between items-center font-bold text-main-font mb-4">
                User Info{" "}
                <img
                  src={userData?.u_profile || img1}
                  className="ms-2 w-8 h-8 md:w-10 md:h-10 rounded-full object-cover"
                  alt="avatar"
                />
              </h1>
              <hr className="border-main-border" />

              {/* User Info Container */}
              <div className="mt-4 md:mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {[
                  {
                    label: "Name",
                    key: "u_name",
                    editable: true,
                    type: "text",
                  },
                  {
                    label: "Email",
                    key: "u_email",
                    editable: true,
                    type: "email",
                  },
                  {
                    label: "Coin",
                    key: "u_coin",
                    editable: true,
                    type: "number",
                  },
                  {
                    label: "Device ID",
                    key: "u_device",
                    editable: true,
                    type: "text",
                  },
                ].map(({ label, key, editable, type }) => (
                  <div
                    key={key}
                    className="p-4 border border-main-border rounded-xl shadow-sm bg-main-card"
                  >
                    <label className="block text-main-font text-sm md:text-base font-semibold mb-2">
                      {label}
                    </label>

                    {isEditing && editable ? (
                      <input
                        type={type}
                        value={userData[key] ?? ""}
                        onChange={(e) =>
                          setUserData({
                            ...userData,
                            [key]:
                              type === "number"
                                ? Number(e.target.value)
                                : e.target.value,
                          })
                        }
                        className="w-full border border-primary-blue rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-purple"
                      />
                    ) : (
                      <p className="text-main-font text-base">
                        {userData[key] || "N/A"}
                      </p>
                    )}
                  </div>
                ))}

                {/* Last Login */}
                <div className="p-4 border border-main-border rounded-xl shadow-sm bg-main-card">
                  <label className="block text-main-font text-sm md:text-base font-semibold mb-2">
                    Last Login
                  </label>
                  <p className="text-main-font">
                    {safeFormat(userData.last_login)}
                  </p>
                </div>

                {/* Created */}
                <div className="p-4 border border-main-border rounded-xl shadow-sm bg-main-card">
                  <label className="block text-main-font text-sm md:text-base font-semibold mb-2">
                    Created
                  </label>
                  <p className="text-main-font">
                    {safeFormat(userData.createdAt)}
                  </p>
                </div>

                {/* Updated */}
                <div className="p-4 border border-main-border rounded-xl shadow-sm bg-main-card">
                  <label className="block text-main-font text-sm md:text-base font-semibold mb-2">
                    Updated
                  </label>
                  <p className="text-main-font">
                    {safeFormat(userData.updatedAt)}
                  </p>
                </div>

                {/* Status Button */}
                <div className="p-4 border border-main-border rounded-xl shadow-sm bg-main-card flex items-center justify-between">
                  <div>
                    <label className="block text-main-font text-sm md:text-base font-semibold mb-2">
                      Status
                    </label>
                    <span className="text-main-font text-base">
                      {userData.u_status ? "Active" : "Blocked"}
                    </span>
                  </div>

                  <button
                    onClick={() =>
                      handleBlockUser(userData._id, !userData.u_status)
                    }
                    className={`px-4 py-2 rounded-lg text-main-white font-semibold flex items-center gap-2 transition ${
                      userData.u_status
                        ? "bg-red-500 hover:bg-red-600"
                        : "bg-green-500 hover:bg-green-600"
                    }`}
                  >
                    {userData.u_status ? <MdBlock /> : <MdCheckCircle />}
                    {userData.u_status ? "Block" : "Unblock"}
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-3 mt-8">
                {isEditing ? (
                  <>
                    <SaveButton onClick={handleSaveChanges} />
                    <CancelButton
                      onClick={() => {
                        setIsEditing(false);
                        fetchUserData();
                      }}
                    />
                  </>
                ) : (
                  <>
                    <EditButton onClick={() => setIsEditing(true)} />
                    <button
                      onClick={() => handleDeleteUser(userData?._id)}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-white bg-red-600 hover:bg-red-700 font-semibold"
                    >
                      <FaTrashAlt />
                      Delete User
                    </button>
                  </>
                )}
              </div>
            </div>

            <hr className="border-main-border border-[2px] mt-2" />

            {/* User Withdraw History */}
            <div className="w-full rounded-lg">
              <div className="p-4 md:p-6 flex flex-col sm:flex-row sm:justify-between">
                <h2 className="text-lg md:text-xl text-main-font font-medium">
                  User Withdraw History
                  <span className="ms-1">({withdrawHistory?.length || 0})</span>
                </h2>
                <p className="text-xs md:text-sm text-gray-500 mt-1">
                  Showing data for the last 20
                </p>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-main-border mt-4">
              <table className="w-full min-w-[850px] text-left">
                <thead className="bg-main-th text-main-font uppercase text-xs md:text-sm">
                  <tr>
                    <th className="ps-4 md:ps-6 py-3 font-semibold">#</th>
                    <th className="p-3 font-semibold">Name</th>
                    <th className="p-3 font-semibold">Type</th>
                    <th className="p-3 font-semibold">Coin</th>
                    <th className="p-3 font-semibold">Total Coin</th>
                    <th className="p-3 font-semibold">Amount</th>
                    <th className="p-3 font-semibold">Status</th>
                    <th className="p-3 font-semibold">Date</th>
                    <th className="p-3 font-semibold rounded-tr-xl">View</th>
                  </tr>
                </thead>

                <tbody className="text-[13px] md:text-sm">
                  {withdrawHistory.length > 0 ? (
                    withdrawHistory.map((user, i) => (
                      <tr
                        key={user._id}
                        className="hover:bg-main-thHover border-b border-main-border transition-all"
                      >
                        <td className="ps-4 md:ps-6 py-3">{i + 1}</td>
                        <td className="p-3 font-medium">
                          {user.u_Name || "N/A"}
                        </td>
                        <td className="p-3">{user.u_type || "N/A"}</td>
                        <td className="p-3">🪙{user.u_wcoin || "0"}</td>
                        <td className="p-3">🪙{user.total_coin || "0"}</td>
                        <td className="p-3 font-semibold">
                          ₹{user.u_Amount || "0"}
                        </td>

                        <td className="p-3">
                          {user.status ? (
                            <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 flex items-center w-fit gap-1">
                              <LuDot size={18} /> Success
                            </span>
                          ) : (
                            <span className="px-3 py-1 rounded-full bg-red-100 text-red-600 flex items-center w-fit gap-1">
                              <LuDot size={18} /> Pending
                            </span>
                          )}
                        </td>

                        <td className="p-3">
                          {formatDateTime(user.createdAt)}
                        </td>

                        <td className="p-3 text-center">
                          <button
                            onClick={() => handleWithdraw(user?._id)}
                            className="bg-main-blue text-white px-3 py-1 rounded-lg hover:bg-main-blue/80 shadow-md"
                          >
                            <IoIosEye size={18} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} className="py-6">
                        <NotFound />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <hr className="border-main-border border-[2px] mt-2" />

            {/* User Coin History */}
            <div className="w-full rounded-lg">
              <div className="p-4 md:p-6 flex flex-col sm:flex-row sm:justify-between">
                <h2 className="text-lg md:text-xl text-main-font font-medium">
                  User Coin History
                  <span className="ms-1">({coinHistory?.length || 0})</span>
                </h2>
                <p className="text-xs md:text-sm text-gray-500 mt-1">
                  Showing data last 100 data
                </p>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-main-border shadow-sm mt-3">
              <table className="w-full min-w-[650px] text-left">
                <thead className="bg-main-th text-main-font uppercase text-xs md:text-sm">
                  <tr>
                    <th className="ps-4 md:ps-6 py-3 font-semibold rounded-tl-xl">
                      #
                    </th>
                    <th className="p-3 font-semibold">Game Name</th>
                    <th className="p-3 font-semibold">Add Coin</th>
                    <th className="p-3 font-semibold">Total Coin</th>
                    <th className="p-3 font-semibold rounded-tr-xl">Date</th>
                  </tr>
                </thead>

                <tbody className="text-[13px] md:text-sm">
                  {coinHistory.length > 0 ? (
                    coinHistory.map((report, i) => (
                      <tr
                        key={report?._id}
                        className="border-b border-main-border hover:bg-main-thHover transition-all"
                      >
                        <td className="ps-4 md:ps-6 py-3">{i + 1}</td>
                        <td className="p-3 font-medium">{report?.game_name}</td>
                        <td className="p-3">🪙{report?.coin_added}</td>
                        <td className="p-3">🪙{report?.total_coin}</td>
                        <td className="p-3">
                          {formatDateTime(report?.createdAt)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="py-6 text-center" colSpan={10}>
                        <NotFound />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserInfo;
